import { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { api } from '../lib/api';
import './ScanQR.css';

const OFFLINE_QUEUE_KEY = 'scanqr_offline_queue_v1';

const loadQueue = () => {
  try { return JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY) || '[]'); }
  catch { return []; }
};
const saveQueue = (q) => {
  try { localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(q)); }
  catch { /* quota or storage error, ignore */ }
};

export default function ScanQR() {
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({ ok: 0, err: 0, total: 0 });
  const [manualCode, setManualCode] = useState('');
  const [evenements, setEvenements] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('all');
  const [cameraError, setCameraError] = useState(null);

  // ── OFFLINE ──
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [pendingQueue, setPendingQueue] = useState(() => loadQueue());
  const [syncing, setSyncing] = useState(false);

  const scannerRef = useRef(null);
  const lastScanRef = useRef({ code: null, at: 0 });
  const flashTimeoutRef = useRef(null);
  const [flashIcon, setFlashIcon] = useState(null); // '✓' or '✕' or '⏳'

  // Charger les événements pour le sélecteur
  useEffect(() => {
    api.get('/evenements')
      .then((r) => setEvenements(r.data || []))
      .catch(() => setEvenements([]));
  }, []);

  // Charger fonts
  useEffect(() => {
    const id = 'scanqr-fonts';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;700;800&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  // Cleanup au démontage
  useEffect(() => () => {
    if (scannerRef.current) {
      scannerRef.current.stop().catch(() => {}).finally(() => {
        try { scannerRef.current.clear(); } catch { /* ignore */ }
      });
    }
    if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
  }, []);

  // ── FLUSH QUEUE : sync les scans en attente quand le réseau revient ──
  const flushQueue = useCallback(async () => {
    const queue = loadQueue();
    if (!queue.length || !navigator.onLine || syncing) return;
    setSyncing(true);
    const remaining = [];
    let synced = 0;
    let okCount = 0;
    let errCount = 0;
    const syncedResults = [];
    for (const item of queue) {
      try {
        const { data } = await api.post('/billets/scan', { qr_code: item.qr_code });
        let valide = !!data.valide;
        let message = data.message || (valide ? 'Entrée validée' : 'Refus');
        if (valide && item.evenement_id && data.billet?.evenement_id !== item.evenement_id) {
          valide = false;
          message = 'Mauvais événement';
        }
        syncedResults.push({
          qr_code: item.qr_code,
          valide,
          message: `[Sync] ${message}`,
          billet: data.billet || null,
          time: new Date(item.queued_at).toLocaleTimeString('fr-FR'),
        });
        synced++;
        if (valide) okCount++; else errCount++;
      } catch {
        remaining.push(item);
      }
    }
    saveQueue(remaining);
    setPendingQueue(remaining);
    if (synced) {
      // Mise à jour des stats globales
      setStats((s) => ({ ok: s.ok + okCount, err: s.err + errCount, total: s.total }));
      // Ajout en tête de l'historique pour retour visuel
      setHistory((h) => [...syncedResults, ...h].slice(0, 50));
    }
    setSyncing(false);
  }, [syncing]);

  // ── Listeners online/offline ──
  useEffect(() => {
    const handleOnline = () => { setOnline(true); flushQueue(); };
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    // Si on est déjà online au mount avec une queue résiduelle, on flush
    if (navigator.onLine) flushQueue();
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [flushQueue]);

  const triggerFlash = (icon) => {
    setFlashIcon(icon);
    if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
    flashTimeoutRef.current = setTimeout(() => setFlashIcon(null), 800);
  };

  const playBeep = (success) => {
    try {
      // eslint-disable-next-line no-undef
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = success ? 880 : 220;
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.start();
      osc.stop(ctx.currentTime + 0.3);
    } catch { /* ignore */ }
  };

  const vibrate = (pattern) => {
    if (navigator.vibrate) navigator.vibrate(pattern);
  };

  const submitScan = async (qr_code, { manual = false } = {}) => {
    if (!qr_code || busy) return;
    // dédup : empêcher les rescan identiques en < 2s (uniquement pour la caméra, pas la saisie manuelle)
    if (!manual) {
      const now = Date.now();
      if (lastScanRef.current.code === qr_code && now - lastScanRef.current.at < 2000) return;
      lastScanRef.current = { code: qr_code, at: now };
    }

    // ── OFFLINE : enregistrer localement, sera synchronisé au retour réseau ──
    if (!navigator.onLine) {
      const pendingItem = {
        qr_code,
        evenement_id: selectedEventId !== 'all' ? selectedEventId : null,
        queued_at: new Date().toISOString(),
      };
      setPendingQueue((prev) => {
        const next = [...prev, pendingItem];
        saveQueue(next);
        return next;
      });
      const result = {
        qr_code,
        valide: null, // null = pending sync
        pending: true,
        message: 'Scan enregistré hors-ligne — sera synchronisé',
        billet: null,
        time: new Date().toLocaleTimeString('fr-FR'),
      };
      setLastResult(result);
      setHistory((h) => [result, ...h].slice(0, 50));
      setStats((s) => ({ ...s, total: s.total + 1 }));
      triggerFlash('⏳');
      playBeep(true); // bip court pour ack visuel
      vibrate([60]);
      return;
    }

    setBusy(true);
    try {
      const { data } = await api.post('/billets/scan', { qr_code });
      const result = {
        qr_code,
        valide: !!data.valide,
        message: data.message || (data.valide ? 'Entrée validée' : 'Refus'),
        billet: data.billet || null,
        time: new Date().toLocaleTimeString('fr-FR'),
      };
      // Filtrer par événement si sélectionné
      if (selectedEventId !== 'all' && result.billet && result.billet.evenement_id !== selectedEventId) {
        result.valide = false;
        result.message = 'Mauvais événement';
      }
      setLastResult(result);
      setHistory((h) => [result, ...h].slice(0, 50));
      setStats((s) => ({
        ok: s.ok + (result.valide ? 1 : 0),
        err: s.err + (result.valide ? 0 : 1),
        total: s.total + 1,
      }));
      triggerFlash(result.valide ? '✓' : '✕');
      playBeep(result.valide);
      vibrate(result.valide ? [120] : [50, 80, 50, 80, 50]);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Erreur réseau';
      const result = { qr_code, valide: false, message: msg, billet: null, time: new Date().toLocaleTimeString('fr-FR') };
      setLastResult(result);
      setHistory((h) => [result, ...h].slice(0, 50));
      setStats((s) => ({ ok: s.ok, err: s.err + 1, total: s.total + 1 }));
      triggerFlash('✕');
      playBeep(false);
      vibrate([50, 80, 50, 80, 50]);
    } finally {
      setBusy(false);
    }
  };

  const startScanner = async () => {
    setCameraError(null);
    try {
      const scanner = new Html5Qrcode('scanqr-reader');
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        (decoded) => { submitScan(decoded); },
        () => { /* scan failures ignorés (frames sans QR) */ },
      );
      setScanning(true);
    } catch (err) {
      console.error('Camera error', err);
      setCameraError(err?.message || "Impossible d'accéder à la caméra (autorisez l'accès dans le navigateur)");
      scannerRef.current = null;
    }
  };

  const stopScanner = async () => {
    if (!scannerRef.current) { setScanning(false); return; }
    try {
      await scannerRef.current.stop();
      scannerRef.current.clear();
    } catch { /* ignore */ }
    scannerRef.current = null;
    setScanning(false);
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualCode.trim()) {
      submitScan(manualCode.trim(), { manual: true });
      setManualCode('');
    }
  };

  const resetStats = () => {
    if (!window.confirm('Réinitialiser les compteurs et l\'historique ?')) return;
    setStats({ ok: 0, err: 0, total: 0 });
    setHistory([]);
    setLastResult(null);
  };

  const selectedEvent = evenements.find((e) => e.id === selectedEventId);

  return (
    <div className="scanqr-root" data-testid="scanqr-root">
      <header className="scanqr-header">
        <h1>
          <span className="pulse"></span>
          Scan QR — Contrôle d'entrée
        </h1>
        <div className="scanqr-header-right">
          <span className={'scanqr-online-badge ' + (online ? 'on' : 'off')} data-testid="online-badge">
            {online ? '🟢 En ligne' : '🔴 Hors-ligne'}
          </span>
          {pendingQueue.length > 0 && (
            <button
              className="scanqr-btn scanqr-btn-ghost"
              style={{ fontSize: '12px', padding: '6px 12px' }}
              onClick={flushQueue}
              disabled={syncing || !online}
              data-testid="btn-sync-now"
            >
              {syncing ? '⏳ Sync...' : `🔄 Synchroniser (${pendingQueue.length})`}
            </button>
          )}
          <div className="scanqr-meta" data-testid="scanqr-meta">
            {scanning ? '📷 Caméra active' : '⏸ Caméra en pause'}
            {selectedEvent && <> · {selectedEvent.nom}</>}
          </div>
        </div>
      </header>

      <div className="scanqr-event-selector">
        <span style={{ color: 'var(--muted)' }}>Événement :</span>
        <select value={selectedEventId} onChange={(e) => setSelectedEventId(e.target.value)} data-testid="event-select">
          <option value="all">Tous les événements</option>
          {evenements.map((e) => (
            <option key={e.id} value={e.id}>{e.nom}</option>
          ))}
        </select>
      </div>

      <div className="scanqr-stats">
        <div className="scanqr-stat ok" data-testid="stat-ok">
          <div className="scanqr-stat-val">{stats.ok}</div>
          <div className="scanqr-stat-lbl">Entrées validées</div>
        </div>
        <div className="scanqr-stat err" data-testid="stat-err">
          <div className="scanqr-stat-val">{stats.err}</div>
          <div className="scanqr-stat-lbl">Refus</div>
        </div>
        <div className="scanqr-stat total" data-testid="stat-total">
          <div className="scanqr-stat-val">{stats.total}</div>
          <div className="scanqr-stat-lbl">Scans</div>
        </div>
      </div>

      <main className="scanqr-main">
        <section className="scanqr-camera-section">
          <div className={'scanqr-camera-box' + (lastResult ? (lastResult.pending ? ' pending' : (lastResult.valide ? ' ok' : ' err')) : '')}>
            <div id="scanqr-reader"></div>
            {!scanning && (
              <div className="scanqr-overlay">
                {cameraError
                  ? <>📷❌<br />{cameraError}<br /><br />Utilisez la saisie manuelle ci-dessous.</>
                  : <>📷<br /><br />Cliquez sur « Démarrer la caméra » pour commencer à scanner.</>}
              </div>
            )}
            {flashIcon && <div className={'scanqr-flash show'} data-testid="scanqr-flash">{flashIcon}</div>}
          </div>

          <div className="scanqr-controls">
            {scanning ? (
              <button className="scanqr-btn scanqr-btn-ghost" onClick={stopScanner} data-testid="btn-stop-camera">
                ⏸ Pause caméra
              </button>
            ) : (
              <button className="scanqr-btn scanqr-btn-primary" onClick={startScanner} data-testid="btn-start-camera">
                📷 Démarrer la caméra
              </button>
            )}
            <button className="scanqr-btn scanqr-btn-ghost" onClick={resetStats} data-testid="btn-reset-stats">
              🔄 Réinitialiser
            </button>
          </div>

          <form className="scanqr-manual" onSubmit={handleManualSubmit}>
            <div className="scanqr-manual-title">Saisie manuelle (si pas de caméra)</div>
            <div className="scanqr-manual-row">
              <input
                type="text"
                placeholder="QR code du billet..."
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                data-testid="manual-input"
              />
              <button className="scanqr-btn scanqr-btn-primary" type="submit" disabled={busy || !manualCode.trim()} data-testid="btn-submit-manual">
                Valider
              </button>
            </div>
          </form>
        </section>

        <section className="scanqr-result-section">
          <div className="scanqr-section-title">Dernier scan</div>
          <div className={'scanqr-last-result' + (lastResult ? (lastResult.pending ? ' pending' : (lastResult.valide ? ' ok' : ' err')) : '')} data-testid="last-result">
            {!lastResult ? (
              <div className="scanqr-last-result-meta">Aucun scan pour le moment.<br />Démarrez la caméra ou saisissez un code manuellement.</div>
            ) : (
              <>
                <div className="scanqr-last-result-icon">{lastResult.pending ? '⏳' : (lastResult.valide ? '✅' : '❌')}</div>
                <div className="scanqr-last-result-msg" data-testid="last-result-msg">{lastResult.message}</div>
                {lastResult.billet ? (
                  <div className="scanqr-last-result-meta">
                    <strong>{lastResult.billet.prenom || ''} {lastResult.billet.user_nom || ''}</strong><br />
                    {lastResult.billet.evt_nom && <>{lastResult.billet.evt_nom} · </>}
                    {lastResult.billet.categorie && <>{lastResult.billet.categorie}</>}
                    {lastResult.billet.siege && <> · {lastResult.billet.siege}</>}
                  </div>
                ) : (
                  <div className="scanqr-last-result-meta">Code : <strong>{lastResult.qr_code}</strong></div>
                )}
              </>
            )}
          </div>

          <div className="scanqr-section-title" style={{ marginTop: '8px' }}>Historique ({history.length})</div>
          <div className="scanqr-history" data-testid="scan-history">
            {history.length === 0 ? (
              <div className="scanqr-history-empty">Aucun scan enregistré.</div>
            ) : (
              history.map((h, i) => (
                <div className="scanqr-history-item" key={i}>
                  <div className={'scanqr-history-icon ' + (h.pending ? 'pending' : (h.valide ? 'ok' : 'err'))}>
                    {h.pending ? '⏳' : (h.valide ? '✓' : '✕')}
                  </div>
                  <div className="scanqr-history-info">
                    <div className={'scanqr-history-msg' + (h.pending ? ' pending' : (h.valide ? '' : ' err'))}>{h.message}</div>
                    <div className="scanqr-history-meta">
                      {h.billet ? `${h.billet.prenom || ''} ${h.billet.user_nom || ''} · ${h.billet.siege || h.billet.categorie || ''}` : h.qr_code}
                    </div>
                  </div>
                  <div className="scanqr-history-time">{h.time}</div>
                </div>
              ))
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
